import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import {
  mrpService,
  type MrpPlannedOrder,
  type MrpProductionSuggestion,
  type MrpRun,
} from '@/services/mrpService';
import { getSuppliers, type PartyResponse } from '@/api/party.api';
import { formatDateIST } from '@/utils/format';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';
import { useWarehouseStore } from '@/stores/warehouseStore';
import ItemTypeBadge from '@/pages/manufacturing/production-orders/components/ItemTypeBadge';

// Formatting helpers
function formatDateDisplay(dateStr?: string | null): string {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return String(dateStr);
  }
}

function formatINR(val?: number): string {
  if (val === undefined || val === null || isNaN(val)) return '₹0.00';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
}

const statusBadges: Record<string, { bg: string; text: string }> = {
  PLANNED: { bg: 'bg-slate-100', text: 'text-slate-700' },
  APPROVED: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  REJECTED: { bg: 'bg-rose-50', text: 'text-rose-700' },
  CONVERTED: { bg: 'bg-blue-50', text: 'text-blue-700' },
};

function isPastToday(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

export default function MRPSuggestionsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();

  const runIdParam = searchParams.get('runId') || '';
  const selectedWarehouseId = useWarehouseStore((s) => s.selectedWarehouseId);
  const selectedWarehouseName = useWarehouseStore((s) => s.selectedWarehouseName);
  const activeWarehouseId = selectedWarehouseId && selectedWarehouseId !== 'ALL' ? selectedWarehouseId : undefined;
  const [runs, setRuns] = useState<MrpRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState(runIdParam);
  const [plannedOrders, setPlannedOrders] = useState<MrpPlannedOrder[]>([]);
  const [productionSuggestions, setProductionSuggestions] = useState<MrpProductionSuggestion[]>([]);
  const [suppliers, setSuppliers] = useState<PartyResponse[]>([]);
  const [loading, setLoading] = useState(false);

  // Tabs: 'PURCHASE' | 'PRODUCTION'
  const [activeTab, setActiveTab] = useState<'PURCHASE' | 'PRODUCTION'>(
    searchParams.get('tab') === 'production' ? 'PRODUCTION' : 'PURCHASE',
  );

  // Filters
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'PURCHASE' | 'PRODUCTION'>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PENDING' | 'APPROVED' | 'CONVERTED' | 'REJECTED'>('ALL');
  const [priorityFilter, setPriorityFilter] = useState<'ALL' | 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW'>('ALL');

  // Selection
  const [selectedPurchaseIds, setSelectedPurchaseIds] = useState<Set<string>>(new Set());
  const [selectedProductionIds, setSelectedProductionIds] = useState<Set<string>>(new Set());
  const [converting, setConverting] = useState(false);

  // Sync selectedRunId with URL search params when runIdParam changes
  useEffect(() => {
    if (runIdParam && runIdParam !== selectedRunId) {
      setSelectedRunId(runIdParam);
    }
  }, [runIdParam]);

  // Fetch runs on mount and whenever activeWarehouseId or runIdParam changes
  useEffect(() => {
    mrpService
      .getRuns(activeWarehouseId ? { warehouseId: activeWarehouseId } : undefined)
      .then((data) => {
        setRuns(data);
        if (data.length > 0) {
          const currentTarget = runIdParam || selectedRunId;
          if (currentTarget && data.some((r) => r.id === currentTarget)) {
            setSelectedRunId(currentTarget);
          } else if (!currentTarget || !data.some((r) => r.id === currentTarget)) {
            setSelectedRunId(data[0].id);
          }
        } else {
          setSelectedRunId(runIdParam || '');
        }
      })
      .catch(() => {});
  }, [activeWarehouseId, runIdParam]);

  useEffect(() => {
    getSuppliers()
      .then((res) => {
        if (res.data) setSuppliers(res.data);
      })
      .catch(() => {});
  }, []);

  // Sync selectedRunId with URL search params
  const handleRunChange = (newRunId: string) => {
    setSelectedRunId(newRunId);
    setSearchParams(newRunId ? { runId: newRunId } : {});
  };

  // Find currently selected run object for display
  const selectedRun = useMemo(() => {
    return runs.find((r) => r.id === selectedRunId) || (selectedRunId ? null : runs[0] || null);
  }, [runs, selectedRunId]);

  // Load data for the selected run and active warehouse
  const loadData = useCallback(() => {
    setLoading(true);

    Promise.all([
      mrpService.getPlannedOrders({ runId: selectedRunId || undefined, warehouseId: activeWarehouseId }),
      mrpService.getProductionSuggestions({ runId: selectedRunId || undefined, warehouseId: activeWarehouseId }).catch(() => []),
    ])
      .then(([orders, prodSuggestions]) => {
        setPlannedOrders(Array.isArray(orders) ? orders : []);
        setProductionSuggestions(Array.isArray(prodSuggestions) ? prodSuggestions : []);
      })
      .catch(() => {
        setPlannedOrders([]);
        setProductionSuggestions([]);
      })
      .finally(() => setLoading(false));
  }, [selectedRunId, activeWarehouseId]);

  useEffect(() => {
    loadData();
  }, [selectedRunId, activeWarehouseId, loadData]);

  // Handle Type filter changing active tab
  useEffect(() => {
    if (typeFilter === 'PURCHASE') {
      setActiveTab('PURCHASE');
    } else if (typeFilter === 'PRODUCTION') {
      setActiveTab('PRODUCTION');
    }
  }, [typeFilter]);

  // Filtered Purchase Orders (converted suggestions shown on top)
  const filteredPurchaseOrders = useMemo(() => {
    const list = plannedOrders.filter((item) => {
      if (typeFilter === 'PRODUCTION') return false;
      if (statusFilter !== 'ALL') {
        const itemStatus = item.status === 'PLANNED' ? 'PENDING' : item.status;
        if (itemStatus !== statusFilter) return false;
      }
      if (priorityFilter !== 'ALL') {
        const itemPriority = item.priority || 'MEDIUM';
        if (itemPriority !== priorityFilter) return false;
      }
      return true;
    });

    return list.sort((a, b) => {
      const aConv = a.status === 'CONVERTED' ? 1 : 0;
      const bConv = b.status === 'CONVERTED' ? 1 : 0;
      if (aConv !== bConv) return bConv - aConv;
      return 0;
    });
  }, [plannedOrders, typeFilter, statusFilter, priorityFilter]);

  // Filtered Production Suggestions (converted suggestions shown on top)
  const filteredProductionSuggestions = useMemo(() => {
    const list = productionSuggestions.filter((item) => {
      if (typeFilter === 'PURCHASE') return false;
      if (statusFilter !== 'ALL') {
        const itemStatus = item.status === 'PLANNED' ? 'PENDING' : item.status;
        if (itemStatus !== statusFilter) return false;
      }
      if (priorityFilter !== 'ALL') {
        const itemPriority = item.priority || 'MEDIUM';
        if (itemPriority !== priorityFilter) return false;
      }
      return true;
    });

    return list.sort((a, b) => {
      const aConv = a.status === 'CONVERTED' ? 1 : 0;
      const bConv = b.status === 'CONVERTED' ? 1 : 0;
      if (aConv !== bConv) return bConv - aConv;
      return 0;
    });
  }, [productionSuggestions, typeFilter, statusFilter, priorityFilter]);

  // Metrics Calculations
  const metrics = useMemo(() => {
    const totalPurchaseValue = plannedOrders.reduce((sum, item) => {
      const rate = Number(item.purchaseRate) || 0;
      const qty = Number(item.planned_qty) || 0;
      return sum + rate * qty;
    }, 0);

    const totalSuggestions = plannedOrders.length + productionSuggestions.length;

    let pending = 0;
    let approved = 0;
    let converted = 0;
    let rejected = 0;

    const all = [...plannedOrders, ...productionSuggestions];
    for (const item of all) {
      const st = item.status === 'PLANNED' ? 'PENDING' : item.status;
      if (st === 'PENDING') pending++;
      else if (st === 'APPROVED') approved++;
      else if (st === 'CONVERTED') converted++;
      else if (st === 'REJECTED') rejected++;
      else pending++;
    }

    return {
      totalPurchaseValue,
      totalSuggestions,
      pending,
      approved,
      converted,
      rejected,
    };
  }, [plannedOrders, productionSuggestions]);

  // Checkbox helpers
  const currentSelectedSet = activeTab === 'PURCHASE' ? selectedPurchaseIds : selectedProductionIds;
  const currentItemsCount = activeTab === 'PURCHASE' ? filteredPurchaseOrders.length : filteredProductionSuggestions.length;
  const isAllSelected = currentItemsCount > 0 && currentSelectedSet.size === currentItemsCount;

  const toggleSelectAll = () => {
    if (activeTab === 'PURCHASE') {
      if (isAllSelected) {
        setSelectedPurchaseIds(new Set());
      } else {
        setSelectedPurchaseIds(new Set(filteredPurchaseOrders.map((i) => i.id)));
      }
    } else {
      if (isAllSelected) {
        setSelectedProductionIds(new Set());
      } else {
        setSelectedProductionIds(new Set(filteredProductionSuggestions.map((i) => i.id)));
      }
    }
  };

  const runOptions: SearchableOption[] = useMemo(() => {
    const list: SearchableOption[] = [
      { value: '', label: activeWarehouseId ? 'All Runs (Active Warehouse)' : 'All Runs (All Warehouses)' }
    ];
    runs.forEach((run) => {
      list.push({
        value: run.id,
        label: `${run.runNumber ? `${run.runNumber} - ` : ''}${formatDateIST(run.runDate)} (${run.planningHorizonDays}d)${run.warehouseName ? ` • ${run.warehouseName}` : ''}`
      });
    });
    return list;
  }, [runs, activeWarehouseId]);

  const statusOptions: SearchableOption[] = useMemo(() => [
    { value: 'All', label: 'All Status' },
    { value: 'PLANNED', label: 'Planned' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'CONVERTED', label: 'Converted' }
  ], []);

  const supplierOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: '— Auto Select —' },
    ...suppliers.map((s) => ({
      value: s.id,
      label: s.name,
      sublabel: s.party_code || s.contact_person,
    })),
  ], [suppliers]);

  const selectedItems = useMemo(() => {
    if (activeTab === 'PURCHASE') {
      return plannedOrders.filter((o) => selectedPurchaseIds.has(o.id));
    } else {
      return productionSuggestions.filter((o) => selectedProductionIds.has(o.id));
    }
  }, [activeTab, plannedOrders, productionSuggestions, selectedPurchaseIds, selectedProductionIds]);

  const hasSelected = selectedItems.length > 0;
  const hasConverted = selectedItems.some((i) => i.status === 'CONVERTED');

  // Rule (Point 5):
  // 1. If converted element selected -> all buttons disabled
  // 2. If approved element selected -> approve disabled, reject & convert enabled
  // 3. If rejected element selected -> only approve enabled, reject & convert disabled
  const canApprove = hasSelected && !hasConverted && selectedItems.some((item) => item.status !== 'APPROVED');
  const canReject = hasSelected && !hasConverted && selectedItems.some((item) => item.status !== 'REJECTED');
  const canConvert = hasSelected && !hasConverted && selectedItems.every((item) => item.status === 'APPROVED');

  const toggleItemSelect = (id: string) => {
    if (activeTab === 'PURCHASE') {
      const next = new Set(selectedPurchaseIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setSelectedPurchaseIds(next);
    } else {
      const next = new Set(selectedProductionIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setSelectedProductionIds(next);
    }
  };

  // Inline row updates
  const handleUpdateQty = async (id: string, newQty: number) => {
    if (newQty <= 0) return;
    setPlannedOrders((prev) =>
      prev.map((o) => (o.id === id ? { ...o, planned_qty: newQty } : o))
    );
    try {
      await mrpService.updatePlannedOrder(id, { planned_qty: newQty });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update quantity');
      loadData();
    }
  };

  const handleUpdateSupplier = async (id: string, supplierId: string) => {
    const matchedSupplier = suppliers.find((s) => s.id === supplierId);
    setPlannedOrders((prev) =>
      prev.map((o) =>
        o.id === id
          ? {
              ...o,
              supplier_id: supplierId || null,
              supplierName: matchedSupplier ? matchedSupplier.name : null,
            }
          : o
      )
    );
    try {
      await mrpService.updatePlannedOrder(id, { supplier_id: supplierId || null });
      toast.success('Supplier updated');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update supplier');
      loadData();
    }
  };

  // Actions: Approve
  const handleApprove = async (id: string) => {
    if (activeTab === 'PURCHASE') {
      setPlannedOrders((prev) =>
        prev.map((o) => (o.id === id ? { ...o, status: 'APPROVED' } : o))
      );
      try {
        await mrpService.approvePlannedOrder(id);
        toast.success('Planned purchase order approved');
        loadData();
      } catch {
        toast.error('Failed to approve order');
        loadData();
      }
    } else {
      setProductionSuggestions((prev) =>
        prev.map((o) => (o.id === id ? { ...o, status: 'APPROVED' } : o))
      );
      try {
        await mrpService.approveProductionSuggestion(id);
        toast.success('Production suggestion approved');
        loadData();
      } catch {
        toast.error('Failed to approve production suggestion');
      }
    }
  };

  const handleConvertSinglePurchase = async (row: any) => {
    try {
      const res = await mrpService.convertPlannedOrders([row.id], activeWarehouseId);
      toast.success(res?.[0]?.poNumber ? `Purchase Order ${res[0].poNumber} created` : 'Purchase order created');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to convert purchase order');
    } finally {
      loadData();
    }
  };

  const handleConvertSingleProduction = async (row: any) => {
    try {
      const res: any = await mrpService.convertProductionSuggestions({
        suggestionIds: [row.id],
        warehouseId: activeWarehouseId,
      });
      const created = res?.[0];
      toast.success(created?.po_number ? `Production Order ${created.po_number} created in DRAFT state` : 'Production order created in DRAFT state');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to convert production order');
    } finally {
      loadData();
    }
  };

  // Actions: Reject
  const handleReject = async (id: string) => {
    try {
      if (activeTab === 'PURCHASE') {
        await mrpService.rejectPlannedOrder(id);
        toast.success('Planned order rejected');
      } else {
        await mrpService.rejectProductionSuggestion(id);
        toast.success('Production suggestion rejected');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to reject');
    } finally {
      loadData();
    }
  };

  // Batch Approve
  const handleBatchApprove = async () => {
    const selectedIds = Array.from(currentSelectedSet);
    if (selectedIds.length === 0) return;
    try {
      if (activeTab === 'PURCHASE') {
        await mrpService.batchApprovePlannedOrders(selectedIds);
        setSelectedPurchaseIds(new Set());
        toast.success(`Approved ${selectedIds.length} order(s)`);
      } else {
        await Promise.all(selectedIds.map((id) => mrpService.approveProductionSuggestion(id)));
        setSelectedProductionIds(new Set());
        toast.success(`Approved ${selectedIds.length} suggestion(s)`);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to approve the selection');
    } finally {
      loadData();
    }
  };

  // Batch Reject
  const handleBatchReject = async () => {
    const selectedIds = Array.from(currentSelectedSet);
    if (selectedIds.length === 0) return;
    try {
      if (activeTab === 'PURCHASE') {
        await mrpService.batchRejectPlannedOrders(selectedIds);
        setSelectedPurchaseIds(new Set());
        toast.success(`Rejected ${selectedIds.length} order(s)`);
      } else {
        await Promise.all(selectedIds.map((id) => mrpService.rejectProductionSuggestion(id)));
        setSelectedProductionIds(new Set());
        toast.success(`Rejected ${selectedIds.length} suggestion(s)`);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to reject the selection');
    } finally {
      loadData();
    }
  };

  // Convert to Orders
  const handleConvert = async () => {
    const selectedIds = Array.from(currentSelectedSet);
    if (selectedIds.length === 0) return;
    setConverting(true);
    try {
      if (activeTab === 'PURCHASE') {
        const res = await mrpService.convertPlannedOrders(selectedIds, activeWarehouseId);
        toast.success(`Created ${res.length} purchase order(s)`);
        setSelectedPurchaseIds(new Set());
      } else {
        const res: any = await mrpService.convertProductionSuggestions({
          suggestionIds: selectedIds,
          warehouseId: activeWarehouseId,
        });
        toast.success(`Created ${Array.isArray(res) ? res.length : 0} production order(s) in DRAFT state`);
        setSelectedProductionIds(new Set());
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to convert the selection');
    } finally {
      setConverting(false);
      loadData();
    }
  };

  const clearFilters = () => {
    setTypeFilter('ALL');
    setStatusFilter('ALL');
    setPriorityFilter('ALL');
  };

  return (
    <AppLayout>
      <div className="p-6 max-w-[1600px] mx-auto min-h-screen bg-[#f8fafc]/50">
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">MRP Suggestions</h1>
              {selectedWarehouseName && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200/80 shadow-xs">
                  <i className="ri-store-2-line text-sm text-indigo-600" />
                  <span>{selectedWarehouseName}</span>
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
              <span>Purchase + Production suggestions from MRP —</span>
              <span
                onClick={() => navigate(`/manufacturing/mrp/shortage?runId=${selectedRun?.id || ''}`)}
                className="text-indigo-600 font-semibold cursor-pointer hover:underline"
              >
                {selectedRun?.runNumber || 'MRP Run'}
              </span>
            </p>
          </div>

          {/* Action Buttons Top Right */}
          <div className="flex items-center gap-2.5">
            <button
              onClick={handleBatchApprove}
              disabled={!canApprove}
              title={
                !hasSelected
                  ? 'Select items first'
                  : hasConverted
                  ? 'Converted items cannot be approved again'
                  : !canApprove
                  ? 'Selected items are already approved'
                  : 'Approve Selected'
              }
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
                canApprove
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer shadow-xs'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed border-none'
              }`}
            >
              <i className="ri-check-line text-base" />
              <span>Approve Selected</span>
            </button>

            <button
              onClick={handleBatchReject}
              disabled={!canReject}
              title={
                !hasSelected
                  ? 'Select items first'
                  : hasConverted
                  ? 'Converted items cannot be rejected'
                  : !canReject
                  ? 'Selected items are already rejected'
                  : 'Reject Selected'
              }
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
                canReject
                  ? 'bg-rose-600 hover:bg-rose-700 text-white cursor-pointer shadow-xs'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed border-none'
              }`}
            >
              <i className="ri-close-line text-base" />
              <span>Reject Selected</span>
            </button>

            <button
              onClick={handleConvert}
              disabled={!canConvert || converting}
              title={
                !hasSelected
                  ? 'Select items first'
                  : hasConverted
                  ? 'Items are already converted'
                  : !canConvert
                  ? 'Only approved items can be converted to orders'
                  : 'Convert to Orders'
              }
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
                canConvert && !converting
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white cursor-pointer shadow-xs'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed border-none'
              }`}
            >
              <i className={`ri-refresh-line text-base ${converting ? 'animate-spin' : ''}`} />
              <span>{converting ? 'Converting...' : 'Convert to Orders'}</span>
            </button>
          </div>
        </div>

        {/* 6 KPI Cards Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5 mb-6">
          {/* 1. Total Purchase Value */}
          <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
            <div className="text-xs font-medium text-slate-500 mb-1.5">Total Purchase Value</div>
            <div className="text-2xl font-bold tracking-tight text-slate-900">
              {formatINR(metrics.totalPurchaseValue)}
            </div>
          </div>

          {/* 2. Total Suggestions */}
          <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
            <div className="text-xs font-medium text-slate-500 mb-1.5">Total Suggestions</div>
            <div className="text-2xl font-bold tracking-tight text-slate-900">
              {metrics.totalSuggestions}
            </div>
          </div>

          {/* 3. Pending */}
          <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
            <div className="text-xs font-medium text-slate-500 mb-1.5">Pending</div>
            <div className="text-2xl font-bold tracking-tight text-slate-900">
              {metrics.pending}
            </div>
          </div>

          {/* 4. Approved */}
          <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
            <div className="text-xs font-medium text-slate-500 mb-1.5">Approved</div>
            <div className="text-2xl font-bold tracking-tight text-emerald-600">
              {metrics.approved}
            </div>
          </div>

          {/* 5. Converted */}
          <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
            <div className="text-xs font-medium text-slate-500 mb-1.5">Converted</div>
            <div className="text-2xl font-bold tracking-tight text-blue-600">
              {metrics.converted}
            </div>
          </div>

          {/* 6. Rejected */}
          <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
            <div className="text-xs font-medium text-slate-500 mb-1.5">Rejected</div>
            <div className="text-2xl font-bold tracking-tight text-rose-600">
              {metrics.rejected}
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center flex-wrap">
            <div className="w-64">
              <SearchableSelect
                value={selectedRunId}
                onChange={(val) => handleRunChange(val)}
                options={runOptions}
                placeholder="Select MRP Run"
                searchPlaceholder="Search MRP Run..."
              />
            </div>

            {/* Type Dropdown */}
            <div className="relative min-w-[130px]">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as any)}
                className="w-full appearance-none bg-white border border-slate-200 text-sm font-medium rounded-lg px-3.5 py-2 pr-8 text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 cursor-pointer"
              >
                <option value="ALL">All Types</option>
                <option value="PURCHASE">Purchase</option>
                <option value="PRODUCTION">Production</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none text-slate-400">
                <i className="ri-arrow-down-s-line text-lg" />
              </div>
            </div>

            <div className="w-40">
              <SearchableSelect
                value={statusFilter}
                onChange={(val) => setStatusFilter(val as typeof statusFilter)}
                options={statusOptions}
                placeholder="All Status"
              />
            </div>

            {/* Priority Dropdown */}
            <div className="relative min-w-[130px]">
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value as any)}
                className="w-full appearance-none bg-white border border-slate-200 text-sm font-medium rounded-lg px-3.5 py-2 pr-8 text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 cursor-pointer"
              >
                <option value="ALL">All Priority</option>
                <option value="URGENT">Urgent</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none text-slate-400">
                <i className="ri-arrow-down-s-line text-lg" />
              </div>
            </div>

            {/* Clear Filters */}
            <button
              onClick={clearFilters}
              className="text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors cursor-pointer ml-1"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Tab Header Row */}
        <div className="border-b border-slate-200 flex gap-8 mb-4">
          <button
            onClick={() => {
              setActiveTab('PURCHASE');
              if (typeFilter === 'PRODUCTION') setTypeFilter('ALL');
            }}
            className={`flex items-center gap-2 pb-3 text-sm font-semibold cursor-pointer transition-colors relative ${
              activeTab === 'PURCHASE'
                ? 'text-indigo-600 border-b-2 border-indigo-600'
                : 'text-slate-500 hover:text-slate-700 border-b-2 border-transparent'
            }`}
          >
            <span>Purchase Suggestions</span>
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-slate-100 text-slate-700">
              {filteredPurchaseOrders.length}
            </span>
          </button>

          <button
            onClick={() => {
              setActiveTab('PRODUCTION');
              if (typeFilter === 'PURCHASE') setTypeFilter('ALL');
            }}
            className={`flex items-center gap-2 pb-3 text-sm font-medium cursor-pointer transition-colors relative ${
              activeTab === 'PRODUCTION'
                ? 'text-indigo-600 border-b-2 border-indigo-600 font-semibold'
                : 'text-slate-500 hover:text-slate-700 border-b-2 border-transparent'
            }`}
          >
            <span>Production Suggestions</span>
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-slate-100 text-slate-600">
              {filteredProductionSuggestions.length}
            </span>
          </button>
        </div>

        {/* Main Table Card */}
        <div className="bg-white border border-slate-200/90 rounded-xl shadow-xs">
          {activeTab === 'PURCHASE' ? (
            /* Purchase Suggestions Table */
            <div className="overflow-x-auto min-h-[460px] pb-44">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-200 text-slate-600 text-xs font-semibold select-none">
                    <th className="py-3.5 px-4 w-10">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 cursor-pointer"
                      />
                    </th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600">Item</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600">Supplier</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600 text-center">Qty</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600">Unit</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600 whitespace-nowrap">Order By</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600 whitespace-nowrap">Required By</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600 whitespace-nowrap">Est. Cost</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600 whitespace-nowrap">Lead Time</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600 text-center">Priority</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600 text-center">Status</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredPurchaseOrders.map((row) => {
                    const isChecked = selectedPurchaseIds.has(row.id);
                    const rate = Number(row.purchaseRate) || 0;
                    const estCost = rate * Number(row.planned_qty || 0);
                    const rowStatus = row.status === 'PLANNED' ? 'PENDING' : row.status;
                    const rowPriority = row.priority || 'MEDIUM';

                    return (
                      <tr
                        key={row.id}
                        className={`hover:bg-slate-50/50 transition-colors ${
                          isChecked ? 'bg-indigo-50/20' : ''
                        }`}
                      >
                        {/* Checkbox */}
                        <td className="py-3.5 px-4">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleItemSelect(row.id)}
                            className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 cursor-pointer"
                          />
                        </td>

                        {/* Item */}
                        <td className="py-3.5 px-4">
                          <div className="font-semibold text-slate-900">{row.item_name}</div>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <ItemTypeBadge type={(row as any).itemType} />
                            <span className="text-xs text-slate-400">
                              {row.itemCode || '—'}
                            </span>
                            {row.warehouseName && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded">
                                <i className="ri-store-2-line text-[9px]" />
                                {row.warehouseName}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Supplier */}
                        <td className="py-3.5 px-4 relative">
                          <div className="min-w-[220px]">
                            <SearchableSelect
                              value={row.supplier_id || ''}
                              onChange={(val) => handleUpdateSupplier(row.id, val)}
                              options={supplierOptions}
                              placeholder="Select Supplier"
                              searchPlaceholder="Search supplier name, code..."
                              direction="down"
                              dropdownWidth="w-full min-w-[260px]"
                            />
                          </div>
                        </td>

                        {/* Qty */}
                        <td className="py-3.5 px-4 text-center">
                          <input
                            type="number"
                            min="0.001"
                            step="any"
                            value={row.planned_qty}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value);
                              handleUpdateQty(row.id, isNaN(val) ? 0 : val);
                            }}
                            className="w-20 px-2 py-1 text-center font-bold text-slate-900 border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                          />
                        </td>

                        {/* Unit */}
                        <td className="py-3.5 px-4 text-slate-600">
                          {row.unit || ''}
                        </td>

                        {/* Order By */}
                        <td className="py-3.5 px-4 text-slate-700 whitespace-nowrap">
                          {formatDateDisplay(row.planned_date)}
                        </td>

                        {/* Required By */}
                        <td className="py-3.5 px-4 text-slate-700 whitespace-nowrap">
                          {formatDateDisplay(row.requiredDate)}
                        </td>

                        {/* Est. Cost */}
                        <td className="py-3.5 px-4 font-semibold text-slate-900 whitespace-nowrap">
                          {formatINR(estCost)}
                        </td>

                        {/* Lead Time */}
                        <td className="py-3.5 px-4 text-slate-600 whitespace-nowrap">
                          {row.leadTimeDays ?? 0} days
                        </td>

                        {/* Priority */}
                        <td className="py-3.5 px-4 text-center whitespace-nowrap">
                          {rowPriority === 'URGENT' ? (
                            <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-rose-50 text-rose-600 border border-rose-200 inline-block">
                              URGENT
                            </span>
                          ) : rowPriority === 'HIGH' ? (
                            <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-amber-50 text-amber-700 border border-amber-200 inline-block">
                              HIGH
                            </span>
                          ) : rowPriority === 'LOW' ? (
                            <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-slate-100 text-slate-600 border border-slate-200 inline-block">
                              LOW
                            </span>
                          ) : (
                            <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-blue-50 text-blue-700 border border-blue-200 inline-block">
                              MEDIUM
                            </span>
                          )}
                        </td>

                        {/* Status (Point 3: Approved green, Rejected red, Converted blue) */}
                        <td className="py-3.5 px-4 text-center whitespace-nowrap">
                          {rowStatus === 'APPROVED' ? (
                            <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 inline-block">
                              APPROVED
                            </span>
                          ) : rowStatus === 'CONVERTED' ? (
                            <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-blue-50 text-blue-700 border border-blue-200 inline-block">
                              CONVERTED
                            </span>
                          ) : rowStatus === 'REJECTED' ? (
                            <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-rose-50 text-rose-700 border border-rose-200 inline-block">
                              REJECTED
                            </span>
                          ) : (
                            <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-slate-100 text-slate-600 border border-slate-200 inline-block">
                              PENDING
                            </span>
                          )}
                        </td>

                        {/* Actions (Point 5) */}
                        <td className="py-3.5 px-4 text-center whitespace-nowrap">
                          <div className="flex items-center justify-center gap-2">
                            {rowStatus === 'APPROVED' ? (
                              <>
                                <button
                                  onClick={() => handleReject(row.id)}
                                  title="Reject"
                                  className="p-1 text-rose-500 hover:bg-rose-50 rounded transition-colors cursor-pointer"
                                >
                                  <i className="ri-close-line text-lg font-bold" />
                                </button>
                                <div className="relative group inline-flex items-center">
                                  <button
                                    onClick={() => handleConvertSinglePurchase(row)}
                                    className="w-8 h-8 rounded-lg bg-[#ede9fe] hover:bg-[#ddd6fe] text-[#6366f1] flex items-center justify-center transition-all shadow-xs cursor-pointer"
                                  >
                                    <i className="ri-settings-3-line text-base font-semibold text-[#6366f1]" />
                                  </button>
                                  {/* Tooltip */}
                                  <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 px-3 py-1.5 bg-[#374151] text-white text-xs font-semibold rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                    Convert to Purchase Order
                                  </div>
                                </div>
                              </>
                            ) : rowStatus === 'CONVERTED' ? (
                              <button
                                onClick={() => navigate(row.converted_po_id ? `/purchase/orders/${row.converted_po_id}` : '/purchase/orders')}
                                className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-full border border-blue-200 transition-colors cursor-pointer"
                                title="View Purchase Order"
                              >
                                <i className="ri-checkbox-circle-fill text-blue-500" /> View PO
                              </button>
                            ) : rowStatus === 'REJECTED' ? (
                              <button
                                onClick={() => handleApprove(row.id)}
                                title="Approve"
                                className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors cursor-pointer"
                              >
                                <i className="ri-check-line text-lg font-bold" />
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleApprove(row.id)}
                                  title="Approve"
                                  className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors cursor-pointer"
                                >
                                  <i className="ri-check-line text-lg font-bold" />
                                </button>
                                <button
                                  onClick={() => handleReject(row.id)}
                                  title="Reject"
                                  className="p-1 text-rose-500 hover:bg-rose-50 rounded transition-colors cursor-pointer"
                                >
                                  <i className="ri-close-line text-lg font-bold" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {!loading && filteredPurchaseOrders.length === 0 && (
                    <tr>
                      <td colSpan={12} className="py-12 text-center text-slate-400">
                        No purchase suggestions match your current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            /* Production Suggestions Table */
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-200 text-slate-600 text-xs font-semibold select-none">
                    <th className="py-3.5 px-4 w-10">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 cursor-pointer"
                      />
                    </th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600">Manufactured Product</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600">BOM Reference</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600 text-center">Planned Qty</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600">Unit</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600 whitespace-nowrap">Start By</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600 whitespace-nowrap">Due Date</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600 whitespace-nowrap">Est. Production Cost</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600 whitespace-nowrap">Lead Time</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600 text-center">Priority</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600 text-center">Status</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-600 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredProductionSuggestions.map((row) => {
                    const isChecked = selectedProductionIds.has(row.id);
                    const rowStatus = row.status === 'PLANNED' ? 'PENDING' : row.status;
                    const rowPriority = row.priority || 'MEDIUM';

                    return (
                      <tr
                        key={row.id}
                        className={`hover:bg-slate-50/50 transition-colors ${
                          isChecked ? 'bg-indigo-50/20' : ''
                        }`}
                      >
                        {/* Checkbox */}
                        <td className="py-3.5 px-4">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleItemSelect(row.id)}
                            className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 cursor-pointer"
                          />
                        </td>

                        {/* Manufactured Product */}
                        <td className="py-3.5 px-4">
                          <div className="font-semibold text-slate-900">{row.item_name}</div>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <ItemTypeBadge type={(row as any).itemType} />
                            <span className="text-xs text-slate-400">{row.itemCode}</span>
                            {row.warehouseName && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded">
                                <i className="ri-store-2-line text-[9px]" />
                                {row.warehouseName}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* BOM */}
                        <td className="py-3.5 px-4">
                          <span className="font-mono text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                            {row.bomCode}
                          </span>
                        </td>

                        {/* Qty */}
                        <td className="py-3.5 px-4 text-center font-bold text-slate-900">
                          {Number(row.qty || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                        </td>

                        {/* Unit */}
                        <td className="py-3.5 px-4 text-slate-600">{row.unit}</td>

                        {/* Start By */}
                        <td className="py-3.5 px-4 text-slate-700 whitespace-nowrap">
                          {formatDateDisplay(row.startDate)}
                        </td>

                        {/* Due Date */}
                        <td className="py-3.5 px-4 text-slate-700 whitespace-nowrap">
                          {formatDateDisplay(row.dueDate)}
                        </td>

                        {/* Est. Cost */}
                        <td className="py-3.5 px-4 font-semibold text-slate-900 whitespace-nowrap">
                          {formatINR(row.estCost)}
                        </td>

                        {/* Lead Time */}
                        <td className="py-3.5 px-4 text-slate-600 whitespace-nowrap">
                          {row.leadTimeDays} days
                        </td>

                        {/* Priority */}
                        <td className="py-3.5 px-4 text-center whitespace-nowrap">
                          {rowPriority === 'URGENT' ? (
                            <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-rose-50 text-rose-600 border border-rose-200 inline-block">
                              URGENT
                            </span>
                          ) : rowPriority === 'HIGH' ? (
                            <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-amber-50 text-amber-700 border border-amber-200 inline-block">
                              HIGH
                            </span>
                          ) : rowPriority === 'LOW' ? (
                            <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-slate-100 text-slate-600 border border-slate-200 inline-block">
                              LOW
                            </span>
                          ) : (
                            <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-blue-50 text-blue-700 border border-blue-200 inline-block">
                              MEDIUM
                            </span>
                          )}
                        </td>

                        {/* Status (Point 3) */}
                        <td className="py-3.5 px-4 text-center whitespace-nowrap">
                          {rowStatus === 'APPROVED' ? (
                            <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 inline-block">
                              APPROVED
                            </span>
                          ) : rowStatus === 'CONVERTED' ? (
                            <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-blue-50 text-blue-700 border border-blue-200 inline-block">
                              CONVERTED
                            </span>
                          ) : rowStatus === 'REJECTED' ? (
                            <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-rose-50 text-rose-700 border border-rose-200 inline-block">
                              REJECTED
                            </span>
                          ) : (
                            <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-slate-100 text-slate-600 border border-slate-200 inline-block">
                              PENDING
                            </span>
                          )}
                        </td>

                        {/* Actions (Point 5) */}
                        <td className="py-3.5 px-4 text-center whitespace-nowrap">
                          <div className="flex items-center justify-center gap-2">
                            {rowStatus === 'APPROVED' ? (
                              <>
                                <button
                                  onClick={() => handleReject(row.id)}
                                  title="Reject"
                                  className="p-1 text-rose-500 hover:bg-rose-50 rounded transition-colors cursor-pointer"
                                >
                                  <i className="ri-close-line text-lg font-bold" />
                                </button>
                                <div className="relative group inline-flex items-center">
                                  <button
                                    onClick={() => handleConvertSingleProduction(row)}
                                    className="w-8 h-8 rounded-lg bg-[#ede9fe] hover:bg-[#ddd6fe] text-[#6366f1] flex items-center justify-center transition-all shadow-xs cursor-pointer"
                                  >
                                    <i className="ri-settings-3-line text-base font-semibold text-[#6366f1]" />
                                  </button>
                                  {/* Tooltip */}
                                  <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 px-3 py-1.5 bg-[#374151] text-white text-xs font-semibold rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                    Convert to Production Order
                                  </div>
                                </div>
                              </>
                            ) : rowStatus === 'CONVERTED' ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-200">
                                <i className="ri-checkbox-circle-fill text-blue-500" /> Done
                              </span>
                            ) : rowStatus === 'REJECTED' ? (
                              <button
                                onClick={() => handleApprove(row.id)}
                                title="Approve"
                                className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors cursor-pointer"
                              >
                                <i className="ri-check-line text-lg font-bold" />
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleApprove(row.id)}
                                  title="Approve"
                                  className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors cursor-pointer"
                                >
                                  <i className="ri-check-line text-lg font-bold" />
                                </button>
                                <button
                                  onClick={() => handleReject(row.id)}
                                  title="Reject"
                                  className="p-1 text-rose-500 hover:bg-rose-50 rounded transition-colors cursor-pointer"
                                >
                                  <i className="ri-close-line text-lg font-bold" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {!loading && filteredProductionSuggestions.length === 0 && (
                    <tr>
                      <td colSpan={12} className="py-12 text-center text-slate-400">
                        No production suggestions match your current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
